import { GraphQL } from "@rpi/openapi-api";
import { reportUnrecognizedStatus } from "../../utils/unrecognizedStatus";

type MoneyCopy = {
  externallyPaid: string;
  refundInFlight: string;
  refundSettled: string;
  settledStatus: GraphQL.PaymentStatus;
};

function moneyCopy(paymentStatus: GraphQL.PaymentStatus | null | undefined, copy: MoneyCopy): string | undefined {
  if (!paymentStatus) return undefined;
  if (paymentStatus === GraphQL.PaymentStatus.ExternallyPaid) return copy.externallyPaid;
  if (paymentStatus === GraphQL.PaymentStatus.ProcessingRefund) return copy.refundInFlight;
  return paymentStatus === copy.settledStatus ? copy.refundSettled : undefined;
}

/** Customer-facing copy for a recorded cancellation outcome, claiming only the money movement the payment status backs. */
export function cancellationOutcomeCopy(
  outcome: GraphQL.CancellationOutcome | null | undefined,
  paymentStatus: GraphQL.PaymentStatus | null | undefined
): string | undefined {
  if (!outcome) return undefined;
  switch (outcome) {
    case GraphQL.CancellationOutcome.RefusedAlreadyShipping:
      return "This order was already shipping and couldn't be cancelled. It's on its way.";
    case GraphQL.CancellationOutcome.CancelledBeforePrint:
      return moneyCopy(paymentStatus, {
        externallyPaid: "Cancelled. This order won't appear on the invoice.",
        refundInFlight: "Cancelled before printing started. A full refund is being processed.",
        refundSettled: "Cancelled before printing started. The payment has been refunded in full.",
        settledStatus: GraphQL.PaymentStatus.RefundedFull,
      });
    case GraphQL.CancellationOutcome.CancelledAfterPrint:
      return moneyCopy(paymentStatus, {
        externallyPaid: "Cancelled. The invoice will include the printed product only, not shipping.",
        refundInFlight: "Printing had already started, so the shipping cost is being refunded. The print cost stands.",
        refundSettled: "Printing had already started, so the shipping cost has been refunded. The print cost stands.",
        settledStatus: GraphQL.PaymentStatus.RefundedShipping,
      });
    default: {
      const unhandled: never = outcome;
      reportUnrecognizedStatus("CancellationOutcome", unhandled);
      return undefined;
    }
  }
}
